import { useState, useEffect } from "react";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Skeleton } from "../../components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { FolderOpen, Plus, Users, Database, AlertTriangle, Link2, Archive } from "lucide-react";
import { toast } from "sonner";

const defaultColors = ["#29B5E8", "#8B5CF6", "#22C55E", "#F59E0B", "#EF4444", "#EC4899", "#F97316", "#14B8A6"];

export default function ProjectsPage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", color: "#1E3A5F" });
  const isAdmin = user?.role === "admin";

  const fetchProjects = async () => {
    try { const { data } = await api.get("/projects"); setProjects(data); } catch (err) { console.error(err); } finally { setLoading(false); }
  };
  useEffect(() => { fetchProjects(); }, []);

  const handleCreate = async () => {
    try {
      await api.post("/projects", form);
      fetchProjects();
      setShowCreate(false);
      setForm({ name: "", description: "", color: "#1E3A5F" });
      toast.success("Project created");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleArchive = async (projectId) => {
    try { await api.delete(`/projects/${projectId}`); fetchProjects(); toast.success("Project archived"); } catch (e) { toast.error("Failed"); }
  };

  if (loading) return <div className="p-6 space-y-4" data-testid="projects-loading"><Skeleton className="h-8 w-48 bg-white/5" /><div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-48 bg-white/5 rounded-xl" />)}</div></div>;

  return (
    <div className="p-6 space-y-6" data-testid="projects-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[26px] font-extrabold tracking-tight text-[#2D3142]">Projects</h1>
          <p className="text-[13px] mt-0.5" style={{ color: "#6B7280" }}>{projects.length} workspaces configured</p>
        </div>
        {isAdmin && (
          <Button className="rounded-lg font-semibold text-sm border-0 text-[#2D3142]" style={{ background: "linear-gradient(135deg, #29B5E8 0%, #1B8FC2 100%)" }} onClick={() => setShowCreate(true)} data-testid="create-project-button">
            <Plus className="w-4 h-4 mr-2" /> New Project
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-grid">
        {projects.map((p, i) => (
          <div key={p.project_id} className="sf-card p-5 animate-fade-up relative" style={{ animationDelay: `${i * 80}ms` }} data-testid={`project-card-${p.project_id}`}>
            <div className="absolute top-0 left-0 right-0 h-1 rounded-t-xl" style={{ background: p.color || "#29B5E8" }} />
            <div className="flex items-start justify-between mb-4 pt-1">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${p.color}15` }}>
                  <FolderOpen className="w-5 h-5" style={{ color: p.color }} />
                </div>
                <div>
                  <h3 className="text-[15px] font-bold text-[#2D3142]">{p.name}</h3>
                  <p className="text-[11px] mt-0.5" style={{ color: "#6B7280" }}>{p.project_id}</p>
                </div>
              </div>
              {!p.is_active && <Badge className="text-[8px] uppercase font-bold border-0 rounded-md" style={{ background: "rgba(190,52,85,0.12)", color: "#BE3455" }}>Archived</Badge>}
            </div>
            <p className="text-[12px] mb-4 leading-relaxed" style={{ color: "#6A7B8E" }}>{p.description}</p>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {[
                { icon: Database, label: "Pipelines", value: p.pipeline_count || 0, color: "#1E3A5F" },
                { icon: AlertTriangle, label: "Incidents", value: p.incident_count || 0, color: "#BE3455" },
                { icon: Link2, label: "Sources", value: p.integration_count || 0, color: "#8B5CF6" },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-2 rounded-lg" style={{ background: "rgba(45,49,66,0.02)" }}>
                  <stat.icon className="w-3.5 h-3.5 mx-auto mb-1" style={{ color: stat.color }} />
                  <div className="text-[16px] font-extrabold text-[#2D3142]">{stat.value}</div>
                  <div className="text-[8px] uppercase tracking-widest font-semibold" style={{ color: "#9CA3AF" }}>{stat.label}</div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <Users className="w-3 h-3" style={{ color: "#6B7280" }} />
                <span className="text-[10px]" style={{ color: "#6B7280" }}>{p.members?.length || 0} members</span>
              </div>
              {isAdmin && p.is_active !== false && (
                <Button variant="ghost" size="sm" className="h-7 text-[11px] rounded-lg text-[#5A6B82] hover:text-[#EF4444] hover:bg-[#EF4444]/10" onClick={() => handleArchive(p.project_id)} data-testid={`archive-project-${p.project_id}`}>
                  <Archive className="w-3 h-3 mr-1" /> Archive
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-[460px]" style={{ background: "#FFFFFF", border: "1px solid #E8E4DF" }} data-testid="create-project-dialog">
          <DialogHeader><DialogTitle className="text-[#2D3142]">Create New Project</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-[11px] font-semibold" style={{ color: "#6B7280" }}>Project Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Marketing Analytics" className="h-10 rounded-lg bg-black/[0.02] border-gray-200 text-[#2D3142]" data-testid="project-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-[11px] font-semibold" style={{ color: "#6B7280" }}>Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What pipelines does this project contain?" className="h-10 rounded-lg bg-black/[0.02] border-gray-200 text-[#2D3142]" data-testid="project-desc-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-[11px] font-semibold" style={{ color: "#6B7280" }}>Color</Label>
              <div className="flex gap-2">
                {defaultColors.map((c) => (
                  <button key={c} onClick={() => setForm({ ...form, color: c })} className="w-8 h-8 rounded-lg transition-all" style={{ background: c, outline: form.color === c ? "2px solid white" : "none", outlineOffset: "2px" }} data-testid={`color-${c}`} />
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-lg border-gray-200" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button className="rounded-lg font-semibold text-sm border-0 text-[#2D3142]" style={{ background: "linear-gradient(135deg, #29B5E8 0%, #1B8FC2 100%)" }} onClick={handleCreate} disabled={!form.name} data-testid="submit-project-button">Create Project</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
